<script setup lang="ts">
import { Tippy } from 'vue-tippy'
import type { NovaKycContentTryYetProps } from './NovaKycContentTryYet.types'
import type { IconType } from '@components/NovaIcon/NovaIcon.types'
import { NovaModalCommon, NovaModalReportCustomer } from '#components'
defineProps<NovaKycContentTryYetProps>()
const { show: modalShow, hide: modalHide } = useModal()
const { t } = useI18n()
const { gtEvent } = useGoogleTag()

const guids = computed<
  { icon: IconType; step: string; tit: string; label: string }[]
>(() => [
  {
    icon: { type: 'outline', name: 'credit-card2' },
    step: t('kyc.unAuthKyc.tryYet.verifyKycProcess.creditCardStep'),
    tit: t('kyc.unAuthKyc.tryYet.verifyKycProcess.creditCardTit'),
    label: t('kyc.unAuthKyc.tryYet.verifyKycProcess.creditCardLabel'),
  },
  {
    icon: { type: 'outline', name: 'face-recognition' },
    step: t('kyc.unAuthKyc.tryYet.verifyKycProcess.faceStep'),
    tit: t('kyc.unAuthKyc.tryYet.verifyKycProcess.faceTit'),
    label: t('kyc.unAuthKyc.tryYet.verifyKycProcess.faceLabel'),
  },
  {
    icon: { type: 'outline', name: 'check2' },
    step: t('kyc.unAuthKyc.tryYet.verifyKycProcess.checkStep'),
    tit: t('kyc.unAuthKyc.tryYet.verifyKycProcess.checkTit'),
    label: t('kyc.unAuthKyc.tryYet.verifyKycProcess.checkLabel'),
  },
])

const handleCsClick = computed(() => {
  modalShow({
    component: NovaModalCommon,
    bind: {
      name: modalsName.MODAL_CS,
      showCloseBtn: true,
    },
    on: {
      close: async () => {
        gtEvent('clickEvent', {
          eventCategory: '클릭',
          eventAction: '고객 문의 모달 닫기',
          eventLabel: t('cs'),
          eventSlot: '유저 패널',
          eventI18nAddr: useKoreanTranslation('cs'),
          eventComponent: 'Button',
        })
        await modalHide(modalsName.MODAL_CS)
      },
    },
    slots: {
      title: t('qna.title'),
      contents: {
        component: NovaModalReportCustomer,
      },
    },
  })
})
</script>

<template>
  <div class="guide">
    <div class="top-txt-box">
      <div
        v-dompurify-html="t(`kyc.unAuthKyc.tryYet.primaryCopy`)"
        class="primary-copy"
      />
      <div class="tooltip-box">
        <p
          v-dompurify-html="t(`kyc.unAuthKyc.tryYet.secondaryCopy`)"
          class="secondary-copy"
        />
        <Tippy :interactive="true" :theme="'gray'" :placement="'bottom'">
          <span class="tooltip-label" />
          <template #content>
            <p v-dompurify-html="t('kyc.unAuthKyc.tryYet.tooltipKyc')" />
          </template>
        </Tippy>
      </div>
    </div>
    <div class="guid-body">
      <div class="guids">
        <div v-for="(guide, index) in guids" :key="index" class="guide-item">
          <div class="icon-box">
            <NovaIcon :icon="guide.icon" />
          </div>
          <div class="text-box">
            <span class="step">{{ guide.step }}</span>
            <span class="tit">{{ guide.tit }}</span>
            <span class="label">{{ guide.label }}</span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-dompurify-html="t('kyc.unAuthKyc.tryYet.cs')"
      class="description mg-t-3u"
      @click="handleCsClick"
    />
  </div>
</template>

<style lang="scss" scoped>
.guide {
  display: flex;
  flex-direction: column;
  gap: 12px;
  .top-txt-box {
    padding-bottom: 16px;
    border-bottom: 1px solid $color-gray-400;
  }
  .primary-copy {
    display: flex;
    justify-content: center;
    @include text-style($text-custom-18-bold);
    color: $color-black;
    line-height: 1.2;
  }
  .tooltip-box {
    display: flex;
    justify-content: center;
    gap: 6px;
    .secondary-copy {
      @include text-style($text-custom-18-bold);
      color: $color-black;
    }
  }

  .guid-body {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 20px;

    .guide-title {
      @include text-style($text-display-medium);
      color: $color-sky-blue;
    }

    .guids {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 20px;
      width: 100%;
      max-width: 216px;
      margin: 0 auto;
      &:before {
        position: absolute;
        top: 0;
        left: 39px;
        content: '';
        width: 2px;
        height: 93%;
        background-color: $color-gray-1;
        z-index: 0;
      }
      .guide-item {
        position: relative;
        display: flex;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 10px;
        z-index: 1;

        .icon-box {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 78px;
          height: 78px;
          border-radius: 50%;
          background-color: $color-gray-1;
          color: $color-black;
        }
        .text-box {
          width: 100%;
          max-width: calc(100% - 88px);
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 4px 0;
          .step {
            color: $color-text-12;
            @include text-style($text-body-12-bold);
            line-height: 1.2;
          }
          .tit {
            color: $color-black;
            @include text-style($text-display-bold);
            line-height: 1.2;
          }
          .label {
            color: $color-gray-500;
            @include text-style($text-custom-12-regular);
            line-height: 1.2;
          }
        }
      }
    }
  }

  .tooltip-box {
    display: flex;
    justify-content: center;
    flex-direction: row;
    align-items: center;
    gap: 0;
    width: 100%;
    @include text-style($text-display-medium);
  }

  .description {
    @include text-style($text-body-14-regular);
    text-align: left;
    line-height: 1.2;
    color: $color-gray-500;
    padding-left: 4px;
    text-indent: -4px;
    word-break: break-all;
  }
}

.tooltip-label {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: $color-bg-custom-11;
  margin-top: 3px;
  &:after {
    content: '?';
    color: $color-bg-3;
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
  }
}
</style>
